import React, { useMemo } from 'react'

import { useSelector } from 'react-redux'

import {
  AccountAvatar,
  Button,
  Drawer,
  Flex,
  Header,
  IconButton,
  InfoBadge,
  Link,
  Text,
} from '@driftt/dds'
import { faArrowLeft } from '@driftt/dds.icons/solid/faArrowLeft'
import { faArrowRight } from '@driftt/dds.icons/solid/faArrowRight'

import config from 'config'
import * as OrgSelectors from 'modules/org/selectors'
import { isNilOrEmpty } from 'utils/ramda'

import { getOpportunityStageVariant } from '../Opportunities.utils'
import { TimelineEventTemplate } from '../TimelineEventTemplate'
import { TimelineSummaryCard } from '../TimelineSummaryCard'

import { DriftIcon, SalesforceIcon } from './OpportunityTimeline.assets'
import {
  isEmailCapturedEvent,
  isMeetingBookedEvent,
} from './OpportunityTimeline.helpers'
import { useOpportunityTimeline } from './OpportunityTimeline.hooks'
import {
  StyledDrawerContent,
  StyledStatusBadge,
} from './OpportunityTimeline.styled'
import {
  OpportunityTimelineProps,
  TimelineEvent,
} from './OpportunityTimeline.types'

export const OpportunityTimeline = ({
  opportunity,
  onClose,
  isLoadingTableData,
  onPrevItem,
  onNextItem,
}: OpportunityTimelineProps) => {
  const { data: opportunityActivities, isLoading } = useOpportunityTimeline({
    opportunityId: opportunity?.opportunityId || '',
  })

  const stageVariant = getOpportunityStageVariant(
    `${opportunity?.opportunityStage}`
  )
  const { name: orgName } = useSelector(OrgSelectors.org)

  const eventsList: TimelineEvent[] = useMemo(
    () =>
      opportunityActivities.map((activity, index) => {
        const endUserName = activity?.endUserDisplayName ?? 'Site Visitor'
        const agentName = activity?.agentDisplayName ?? `someone at ${orgName}`

        const emailCapturedHeader = `Email captured ${
          activity?.agentDisplayName ? `by ${activity?.agentDisplayName}` : ''
        }`

        const convoCapturedHeader = (() => {
          if (!activity?.endUserDisplayName && !activity?.agentDisplayName) {
            return 'Conversation Created'
          }
          return `${endUserName} talked to ${agentName}`
        })()

        const meetingBookedHeader = (() => {
          if (!activity?.endUserDisplayName && !activity?.agentDisplayName) {
            return 'Meeting Booked'
          }
          return `${endUserName} scheduled a meeting with ${agentName}`
        })()

        return {
          date: activity?.eventDateAt, // NOTE: same as conversation created at, shouldn't use with zone as it could lead to sorting issues
          component: (
            <TimelineEventTemplate
              key={`timeline-event-template-${activity?.conversationId}-${index}`}
              headerLeftElement={
                <>
                  {activity?.leadCaptureSource ? (
                    <SalesforceIcon />
                  ) : (
                    <DriftIcon />
                  )}
                </>
              }
              headerRightElement={
                <InfoBadge
                  variant={
                    activity?.leadCaptureSource ? 'secondary' : 'primary'
                  }
                >
                  {activity?.leadCaptureSource ? 'Sourced' : 'Influenced'}
                </InfoBadge>
              }
              header={
                isEmailCapturedEvent(activity?.eventType)
                  ? emailCapturedHeader
                  : isMeetingBookedEvent(activity?.eventType)
                  ? meetingBookedHeader
                  : convoCapturedHeader
              }
              date={activity?.eventDateAtWithZone}
            >
              {activity?.playbookId && (
                <Text>
                  Playbook:
                  <Link
                    variant="inline"
                    target="__blank"
                    href={`${config.APP_URL}/reports/playbooks/chat/${activity?.playbookId}`}
                  >
                    {activity?.playbookName}
                  </Link>
                </Text>
              )}
              {isEmailCapturedEvent(activity?.eventType) && (
                <Text>
                  {'Email: '}
                  {activity?.endUserEmail}
                </Text>
              )}
              {activity?.conversationId && (
                <Link
                  href={`${config.APP_URL}/conversations/${activity?.conversationId}`}
                  target="__blank"
                  variant="inline"
                  external={true}
                >
                  View live chat
                </Link>
              )}
            </TimelineEventTemplate>
          ),
        } as TimelineEvent
      }),
    [opportunityActivities, orgName]
  )

  const closeOpportunity: TimelineEvent = {
    date: opportunity?.opportunityClosedAt || null, // NOTE: using closedAt to make an accurate sorting along with the dimension used to get all data sorted from cube
    component: (
      <TimelineEventTemplate
        headerLeftElement={<SalesforceIcon />}
        header={
          <Text variant="body">
            Opportunity has closed in <b>Salesforce</b>
          </Text>
        }
        headerRightElement={
          <StyledStatusBadge variant={stageVariant} size="small">
            {opportunity?.opportunityStage}
          </StyledStatusBadge>
        }
        date={`${opportunity?.opportunityClosedAtWithZone}`}
      />
    ),
  }

  const createOpportunity: TimelineEvent = {
    date: opportunity?.opportunityCreatedAt || null, // NOTE: using closedAt to make an accurate sorting along with the dimension used to get all data sorted from cube
    component: (
      <TimelineEventTemplate
        headerLeftElement={<SalesforceIcon />}
        header={
          <Text variant="body">
            Opportunity created in <b>Salesforce</b>
          </Text>
        }
        date={`${opportunity?.opportunityCreatedAtWithZone}`}
      />
    ),
  }

  if (!isLoading) {
    // NOTE: should add create and closed only once it finished loading
    eventsList.push(createOpportunity)
    eventsList.push(closeOpportunity)

    if (eventsList.length > 0) {
      // NOTE: only makes sense to sort if there's events in the list. Otherwise, create and closed should already be sorted
      eventsList.sort((eventA, eventB) => {
        return (
          Number(new Date(eventB.date || '')) -
          Number(new Date(eventA.date || ''))
        )
      })
    }
  }

  return (
    <Drawer.Root
      open={!isNilOrEmpty(opportunity?.opportunityId)}
      onClose={onClose}
    >
      <Drawer.Dialog size="large" preventContentAutoFocusOnOpen={true}>
        <Drawer.Title
          prefix={
            <Flex direction="column" fullWidth alignItems="flex-start">
              <Flex
                direction="row"
                gap={2}
                fullWidth
                alignItems="center"
                justifyContent="flex-start"
              >
                <IconButton
                  title=""
                  icon={faArrowLeft}
                  onClick={onPrevItem}
                  disabled={isLoadingTableData}
                />
                <IconButton
                  title=""
                  icon={faArrowRight}
                  onClick={onNextItem}
                  disabled={isLoadingTableData}
                />
              </Flex>
              <Flex
                direction="row"
                gap={1}
                fullWidth
                alignItems="center"
                justifyContent="flex-start"
              >
                <AccountAvatar name={opportunity?.companyName} size="medium" />
                <Flex
                  direction="column"
                  gap={0}
                  fullWidth
                  alignItems="flex-start"
                  justifyContent="center"
                >
                  <Header variant="h3">{opportunity?.companyName}</Header>
                  <Link
                    href="https://www.drift.com"
                    target="_blank"
                    type="inline"
                    size="small"
                  >
                    {opportunity?.opportunityDomain}
                  </Link>
                </Flex>
              </Flex>
            </Flex>
          }
          title=""
        />
        <StyledDrawerContent>
          <Flex gap={2} direction="column">
            <TimelineSummaryCard
              title={opportunity?.opportunityName || '--'}
              isInfluenced={!!opportunity?.isInfluenced}
              isSourced={!!opportunity?.isSourced}
              type={`${opportunity?.opportunityType}`}
              totalArr={`${opportunity?.opportunityAmount}`}
              opportunityStage={`${opportunity?.opportunityStage}`}
              createdDate={`${opportunity?.opportunityCreatedAtWithZone}`}
              lastUpdateDate={`${opportunity?.opportunityClosedAtWithZone}`}
            />
            {/* RENDER SKELETONS WHEN LOADING */}
            {isLoading &&
              Array(6)
                .fill(1)
                .map((_, index) => (
                  <TimelineEventTemplate
                    key={`timeline-event-template-skeleton-${index}`}
                    isLoading={true}
                  />
                ))}
            {/* RENDER ALL EVENTS */}
            {eventsList.map((event) => {
              return event.component
            })}
          </Flex>
        </StyledDrawerContent>
        <Drawer.Actions>
          <Flex fullWidth justifyContent="flex-end" alignItems="center">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </Flex>
        </Drawer.Actions>
      </Drawer.Dialog>
    </Drawer.Root>
  )
}
